import { ref, onMounted, onUnmounted } from 'vue'
import { useQuasar } from 'quasar'

// Simple SPA update checker that polls a static version file.
// When the version changes during a session, users are prompted to reload.
export function useSpaUpdate(options = {}) {
  const {
    versionUrl = '/app-version.json',
    pollIntervalMs = 5 * 60 * 1000 // default: 5 minutes
  } = options

  const $q = useQuasar()

  const currentVersion = ref(null)
  const latestVersion = ref(null)
  const checking = ref(false)
  const error = ref(null)

  let timerId = null
  let notificationId = null

  const fetchVersion = async () => {
    try {
      checking.value = true
      error.value = null

      const response = await fetch(`${versionUrl}?t=${Date.now()}`, {
        cache: 'no-store'
      })

      if (!response.ok) {
        throw new Error(`Failed to fetch version (${response.status})`)
      }

      const data = await response.json()
      return data.version || null
    } catch (err) {
      error.value = err.message || String(err)
      // Don't spam notifications; just log for now.
      console.warn('[SpaUpdate] Version check failed:', err)
      return null
    } finally {
      checking.value = false
    }
  }

  const showUpdateNotification = () => {
    // Avoid duplicate notifications
    if (notificationId !== null) {
      return
    }

    const notif = $q.notify({
      type: 'info',
      message: 'A new version of Blu is available.',
      caption: 'Click “Reload now” to update to the latest version.',
      position: 'top',
      timeout: 0, // persistent until user acts or reloads
      actions: [
        {
          label: 'Reload now',
          color: 'primary',
          handler: () => {
            // Hard reload to ensure we bypass any caches
            window.location.reload(true)
          }
        },
        {
          label: 'Later',
          color: 'white'
        }
      ]
    })

    // Keep a handle in case we need to dismiss later
    notificationId = notif
  }

  const checkForUpdate = async () => {
    const remoteVersion = await fetchVersion()
    if (!remoteVersion) {
      return
    }

    if (currentVersion.value == null) {
      // First successful fetch for this session
      currentVersion.value = remoteVersion
      latestVersion.value = remoteVersion
      return
    }

    latestVersion.value = remoteVersion

    if (remoteVersion !== currentVersion.value) {
      showUpdateNotification()
    }
  }

  onMounted(() => {
    // Skip in Electron where a dedicated auto-update flow already exists
    if (typeof window !== 'undefined' && window.electronAPI) {
      return
    }

    // Initial check
    checkForUpdate()

    // Periodic polling
    timerId = window.setInterval(() => {
      checkForUpdate()
    }, pollIntervalMs)
  })

  onUnmounted(() => {
    if (timerId) {
      window.clearInterval(timerId)
      timerId = null
    }
    notificationId = null
  })

  return {
    currentVersion,
    latestVersion,
    checking,
    error,
    checkForUpdate
  }
}


